import { Controller } from "@hotwired/stimulus"

// Moves cards between All Cards kanban columns when their state changes via Turbo Streams.
// Listens for turbo:before-stream-render events and re-sorts cards into the correct column
// based on their data-state attribute.
export default class extends Controller {
  static targets = ["todoColumn", "startedColumn", "completedColumn",
                    "reviewAllTab", "reviewNoneTab", "reviewDecidingTab", "reviewApprovedTab", "reviewRejectedTab"]

  connect() {
    this.currentReviewFilter = ""
    this.boundHandleStreamRender = this.handleStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundHandleStreamRender)
    if (this.hasReviewAllTabTarget) this.setActiveReviewTab(this.reviewAllTabTarget)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.boundHandleStreamRender)
  }

  handleStreamRender() {
    requestAnimationFrame(() => this.resortColumns())
  }

  resortColumns() {
    if (!this.hasTodoColumnTarget || !this.hasStartedColumnTarget || !this.hasCompletedColumnTarget) return

    this.adoptOrphanedCards()

    const columns = [
      { el: this.todoColumnTarget, state: "todo" },
      { el: this.startedColumnTarget, state: "started" },
      { el: this.completedColumnTarget, state: "ended" }
    ]

    columns.forEach(({ el, state }) => {
      el.querySelectorAll("turbo-frame").forEach(frame => {
        const card = frame.querySelector("[data-state]")
        if (!card) return
        if (card.dataset.state !== state) {
          const correctColumn = this.columnForState(card.dataset.state)
          if (correctColumn) {
            // Find the card list container inside the target column, or use column itself
            const container = correctColumn.querySelector(".space-y-2") || correctColumn
            container.appendChild(frame)
          }
        }
      })
    })

    this.updateCounts()
  }

  // Move newly broadcast cards from the hidden inbox into the correct column
  adoptOrphanedCards() {
    const inbox = document.getElementById("all_cards_new_arrivals")
    if (!inbox) return

    inbox.querySelectorAll("turbo-frame").forEach(frame => {
      const card = frame.querySelector("[data-state]")
      if (!card) return

      const column = this.columnForState(card.dataset.state)
      if (column) {
        const container = column.querySelector(".space-y-2") || column
        container.prepend(frame)
      }
    })
  }

  columnForState(state) {
    switch (state) {
      case "todo": return this.hasTodoColumnTarget ? this.todoColumnTarget : null
      case "started": return this.hasStartedColumnTarget ? this.startedColumnTarget : null
      case "ended": return this.hasCompletedColumnTarget ? this.completedColumnTarget : null
      default: return null
    }
  }

  filterReviewAll(event) {
    event.preventDefault()
    this.currentReviewFilter = ""
    this.setActiveReviewTab(this.reviewAllTabTarget)
    this.applyReviewFilter()
  }

  filterReviewNone(event) {
    event.preventDefault()
    this.currentReviewFilter = "none"
    this.setActiveReviewTab(this.reviewNoneTabTarget)
    this.applyReviewFilter()
  }

  filterReviewDeciding(event) {
    event.preventDefault()
    this.currentReviewFilter = "deciding"
    this.setActiveReviewTab(this.reviewDecidingTabTarget)
    this.applyReviewFilter()
  }

  filterReviewApproved(event) {
    event.preventDefault()
    this.currentReviewFilter = "approved"
    this.setActiveReviewTab(this.reviewApprovedTabTarget)
    this.applyReviewFilter()
  }

  filterReviewRejected(event) {
    event.preventDefault()
    this.currentReviewFilter = "rejected"
    this.setActiveReviewTab(this.reviewRejectedTabTarget)
    this.applyReviewFilter()
  }

  setActiveReviewTab(activeTab) {
    const tabs = [
      this.reviewAllTabTarget, this.reviewNoneTabTarget, this.reviewDecidingTabTarget,
      this.reviewApprovedTabTarget, this.reviewRejectedTabTarget
    ]
    tabs.forEach(tab => {
      tab.classList.remove("bg-sapphire", "text-white")
      tab.classList.add("bg-gray-100", "text-gray-600")
    })
    activeTab.classList.remove("bg-gray-100", "text-gray-600")
    activeTab.classList.add("bg-sapphire", "text-white")
  }

  applyReviewFilter() {
    if (!this.hasTodoColumnTarget || !this.hasStartedColumnTarget || !this.hasCompletedColumnTarget) return

    const allColumns = [this.todoColumnTarget, this.startedColumnTarget, this.completedColumnTarget]
    allColumns.forEach(column => {
      column.querySelectorAll("turbo-frame").forEach(frame => {
        const card = frame.querySelector("[data-review-status]")
        if (!card) return
        const reviewStatus = card.dataset.reviewStatus
        if (this.currentReviewFilter === "" || reviewStatus === this.currentReviewFilter) {
          frame.classList.remove("hidden")
        } else {
          frame.classList.add("hidden")
        }
      })
    })
  }

  updateCounts() {
    if (!this.hasTodoColumnTarget || !this.hasStartedColumnTarget || !this.hasCompletedColumnTarget) return

    const countCards = (el) => el.querySelectorAll("turbo-frame").length

    const todoSection = document.getElementById("todo_cards_section")
    const startedSection = document.getElementById("started_cards_section")
    const completedSection = document.getElementById("completed_cards_section")

    if (todoSection) {
      const badge = todoSection.querySelector(".rounded-full")
      if (badge) badge.textContent = countCards(this.todoColumnTarget)
    }
    if (startedSection) {
      const badge = startedSection.querySelector(".rounded-full")
      if (badge) badge.textContent = countCards(this.startedColumnTarget)
    }
    if (completedSection) {
      const badge = completedSection.querySelector(".rounded-full")
      if (badge) badge.textContent = countCards(this.completedColumnTarget)
    }
  }
}
